import React, { useState, useEffect, useRef } from "react";
import {
  AIMedicationEntryProps,
  AIStatus,
  RecognizedMedication,
} from "./types";
import { MedicationInput } from "./MedicationInput";
import { useMedicationAI, generateGridValue } from "./useMedicationAI";
import { makeBusinessDecision } from "./businessLogic";
import { PreScreeningQuestion } from "../../../types/landingPage";
import { track } from "../../../utils/rudderstack";
import "./AIMedicationEntry.css";
import { withScope, captureException } from "@sentry/react";

export const AIMedicationEntry: React.FC<AIMedicationEntryProps> = ({
  question,
  value,
  onAnswer,
  onDisqualify,
  allowBack,
  onBack,
  onNext,
  landingPageId,
  variantId,
  masterId,
  prescreenerId,
}) => {
  const typedQuestion = question as PreScreeningQuestion;

  // Initialize originalText from saved value if available
  const [originalText, setOriginalText] = useState(() => {
    if (value && typeof value === "string" && value !== "[]") {
      try {
        const parsed = JSON.parse(value);
        // Check if it's our new format with rawText
        if (parsed.rawText) {
          return parsed.rawText;
        }
        // Legacy format - just medications without rawText
      } catch (e) {
        withScope((scope) => {
          scope.setExtra('questionId', typedQuestion.id);
          scope.setExtra('errorMessage', e instanceof Error ? e.message : String(e));
          scope.setTag('component', 'AIMedicationEntry');
          scope.setTag('error', 'json_parse_init');
          captureException(e);
        });
      }
    }
    return "";
  });
  const [clarificationMessage, setClarificationMessage] = useState<
    string | null
  >(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isProcessingFlow, setIsProcessingFlow] = useState(false);
  const hasAutoAdvancedRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionIdRef = useRef<string>("");

  const {
    status,
    error,
    analyzeMedications,
    confirmMedications,
    reset,
    sessionId,
  } = useMedicationAI(typedQuestion.aiConfig);

  // Store sessionId in ref for tracking
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // Handle medication analysis and seamless flow
  const handleAnalyzeMedications = async (text: string) => {
    // Prevent double submissions
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setOriginalText(text);
    setClarificationMessage(null);
    hasAutoAdvancedRef.current = false;
    setIsProcessingFlow(true);

    let result = await analyzeMedications(text);

    if (!result) {
      setIsProcessingFlow(false);
      isSubmittingRef.current = false;
      return;
    }

    // Check if user indicated no medications
    const noMedicationPhrases = [
      "nothing",
      "none",
      "no medications",
      "no meds",
      "n/a",
      "not taking",
      "nothing currently",
      "none currently",
      "no current medications",
      "i don't take",
      "don't take any",
      "not on any",
    ];

    // Hostile/evasive responses should NOT be treated as "no medications"
    const hostilePatterns = [
      "none of your business",
      "why do you need",
      "don't want to tell",
      "that's private",
      "i'd rather not",
      "not comfortable",
      "it doesn't matter",
      "not important",
    ];

    const textLower = text.toLowerCase().trim();
    const isHostile = hostilePatterns.some((pattern) =>
      textLower.includes(pattern)
    );
    const isNoMedications =
      !isHostile &&
      noMedicationPhrases.some(
        (phrase) => textLower === phrase || textLower.includes(phrase)
      );

    if (isNoMedications && result.medications.length === 0) {
      handleNoMedications();
      setIsProcessingFlow(false);
      isSubmittingRef.current = false;
      return;
    }

    // Handle the response based on confidence and validation
    const avgConfidence =
      result.medications.reduce((sum, med) => sum + med.confidence, 0) /
        result.medications.length || 0;
    const needsClarification =
      result.needsClarification && result.needsClarification.length > 0;

    // Map medications with grid values (same as in analyzeMedications)
    const mappedMedications = result.medications.map((med) => ({
      ...med,
      gridValue: generateGridValue(med, typedQuestion.aiConfig),
    }));

    // Run business logic immediately (synchronous)
    // IMPORTANT: Business logic is the source of truth for pass/fail decisions
    // The validation API is only for additional insights and monitoring
    const decision = makeBusinessDecision(
      mappedMedications,
      typedQuestion.aiConfig,
      result.needsClarification
    );
    
    // Always track the business decision
    track("lp.ai_medication_business_decision", {
      sessionId: sessionIdRef.current,
      decision: decision.decision,
      flaggedCount: decision.flaggedMedications.length,
      clarificationCount: decision.clarificationNeeded?.length || 0,
      medicationCount: mappedMedications.length,
      variant: "ai",
      flowType: "seamless",
      landing_page_id: landingPageId || "unknown",
      variant_id: variantId || landingPageId || "unknown",
      master_id: masterId || landingPageId || "unknown",
      prescreener_id: prescreenerId || landingPageId || "unknown",
    });

    // Check if business logic says clarify first
    if (decision.decision === "clarify") {
      const message =
        decision.clarificationNeeded?.[0]?.suggestedQuestion ||
        "Please provide more information about your medications";
      setClarificationMessage(message);
      
      // Also track specific clarification event
      track("lp.ai_medication_clarification_from_business_logic", {
        sessionId: sessionIdRef.current,
        clarificationRequests: decision.clarificationNeeded,
        variant: "ai",
        flowType: "seamless",
        landing_page_id: landingPageId || "unknown",
        variant_id: variantId || landingPageId || "unknown",
        master_id: masterId || landingPageId || "unknown",
        prescreener_id: prescreenerId || landingPageId || "unknown",
      });
      reset();
      setIsProcessingFlow(false);
      isSubmittingRef.current = false;
      return;
    }

    // Check if business logic says disqualify
    if (decision.decision === "fail") {
      // Track that we're waiting for validation due to business logic failure
      track("lp.ai_medication_waiting_for_validation", {
        sessionId: sessionIdRef.current,
        businessDecision: decision.decision,
        flaggedMedications: decision.flaggedMedications.map((f) => ({
          name: f.medication.name,
          reason: f.reason,
        })),
        variant: "ai",
        flowType: "seamless",
        landing_page_id: landingPageId || "unknown",
        variant_id: variantId || landingPageId || "unknown",
        master_id: masterId || landingPageId || "unknown",
        prescreener_id: prescreenerId || landingPageId || "unknown",
      });

      // Show loading state while we wait for validation
      // (The MedicationInput component already shows spinner during PROCESSING state)

      // Wait for validation API - it might overturn the decision
      const startTime = Date.now();
      try {
        const validatedDecision = await confirmMedications(
          mappedMedications,
          text // Use the text parameter directly, not the state
        );
        const validationDuration = Date.now() - startTime;

        // Use the validation decision if available, otherwise fall back to business logic
        const finalDecision = validatedDecision || decision;

        if (finalDecision.decision === "fail") {
          // Track final disqualification
          track("lp.ai_medication_final_disqualification", {
            sessionId: sessionIdRef.current,
            businessDecision: decision.decision,
            validationDecision: validatedDecision?.decision,
            validationDuration,
            flaggedMedications: decision.flaggedMedications.map(
              (f) => f.medication.name
            ),
            variant: "ai",
            flowType: "seamless",
            landing_page_id: landingPageId || "unknown",
            variant_id: variantId || landingPageId || "unknown",
            master_id: masterId || landingPageId || "unknown",
            prescreener_id: prescreenerId || landingPageId || "unknown",
          });

          const message =
            finalDecision.reasoning ||
            typedQuestion.disqualifyMessage ||
            "This study cannot include people taking certain medications";

          if (onDisqualify) {
            onDisqualify(message);
          }

          // Save answer data AFTER onDisqualify so it isn't overwritten
          // by the stale value prop in QuestionRenderer's onDisqualify handler
          const answerData = buildAnswerData(mappedMedications, originalText);
          onAnswer(typedQuestion.id, JSON.stringify(answerData));
          setIsProcessingFlow(false);
          isSubmittingRef.current = false;
          return;
        } else {
          // Validation overturned the business logic! User can continue
          track("lp.ai_medication_validation_override_pass", {
            sessionId: sessionIdRef.current,
            businessDecision: decision.decision,
            validationDecision: finalDecision.decision,
            validationDuration,
            medications: mappedMedications.map((m) => m.name),
            flaggedByBusiness: decision.flaggedMedications.map(
              (f) => f.medication.name
            ),
            variant: "ai",
            flowType: "seamless",
            landing_page_id: landingPageId || "unknown",
            variant_id: variantId || landingPageId || "unknown",
            master_id: masterId || landingPageId || "unknown",
            prescreener_id: prescreenerId || landingPageId || "unknown",
          });

          // Continue with normal flow
          handleAutoAdvance(mappedMedications);
          setIsProcessingFlow(false);
          isSubmittingRef.current = false;
          return;
        }
      } catch (error) {
        console.error(
          "[Seamless Flow] Validation error, using business logic decision:",
          error
        );
        withScope((scope) => {
          scope.setExtra('sessionId', sessionIdRef.current);
          scope.setExtra('businessDecision', decision.decision);
          scope.setExtra('medicationCount', mappedMedications.length);
          scope.setExtra('flaggedCount', decision.flaggedMedications.length);
          scope.setTag('component', 'AIMedicationEntry');
          scope.setTag('error', 'validation_api_fail');
          captureException(error);
        });

        // Track validation API failure
        track("lp.ai_medication_validation_api_error", {
          sessionId: sessionIdRef.current,
          businessDecision: decision.decision,
          validationDuration: Date.now() - startTime,
          error: error instanceof Error ? error.message : "Unknown error",
          variant: "ai",
          flowType: "seamless",
          landing_page_id: landingPageId || "unknown",
          variant_id: variantId || landingPageId || "unknown",
          master_id: masterId || landingPageId || "unknown",
          prescreener_id: prescreenerId || landingPageId || "unknown",
        });

        // If validation fails, fall back to business logic decision
        const message =
          decision.reasoning ||
          typedQuestion.disqualifyMessage ||
          "This study cannot include people taking certain medications";

        if (onDisqualify) {
          onDisqualify(message);
        }

        // Save answer data AFTER onDisqualify so it isn't overwritten
        const answerData = buildAnswerData(mappedMedications, originalText);
        onAnswer(typedQuestion.id, JSON.stringify(answerData));
        setIsProcessingFlow(false);
        isSubmittingRef.current = false;
        return;
      }
    }

    // Determine user flow based on confidence AND business decision
    if (
      avgConfidence >= 0.85 &&
      !needsClarification &&
      decision.decision === "pass"
    ) {
      // High confidence + passing: auto-advance immediately
      track("lp.ai_medication_auto_advance", {
        sessionId: sessionIdRef.current,
        avgConfidence,
        medicationCount: result.medications.length,
        businessDecision: decision.decision,
        variant: "ai",
        flowType: "seamless",
        landing_page_id: landingPageId || "unknown",
        variant_id: variantId || landingPageId || "unknown",
        master_id: masterId || landingPageId || "unknown",
        prescreener_id: prescreenerId || landingPageId || "unknown",
      });
      handleAutoAdvance(mappedMedications);
      setIsProcessingFlow(false);
      isSubmittingRef.current = false;

      // Run validation in background for monitoring/logging only
      confirmMedications(mappedMedications, text)
        .then((validatedDecision) => {
          // Track if validation disagrees (for monitoring)
          if (
            validatedDecision &&
            validatedDecision.decision !== decision.decision
          ) {
            track("lp.ai_medication_validation_disagreement", {
              sessionId: sessionIdRef.current,
              businessDecision: decision.decision,
              validationDecision: validatedDecision.decision,
              medications: mappedMedications.map((m) => m.name),
              variant: "ai",
              flowType: "seamless",
              landing_page_id: landingPageId || "unknown",
              variant_id: variantId || landingPageId || "unknown",
              master_id: masterId || landingPageId || "unknown",
              prescreener_id: prescreenerId || landingPageId || "unknown",
            });
          }
        })
        .catch((error) => {
          console.error("[Seamless Flow] Validation error:", error);
          withScope((scope) => {
            scope.setExtra('sessionId', sessionIdRef.current);
            scope.setExtra('confidence', avgConfidence);
            scope.setExtra('decision', decision.decision);
            scope.setTag('component', 'AIMedicationEntry');
            scope.setTag('error', 'background_validation_high');
            captureException(error);
          });
          // Don't disrupt the user flow for API errors
        });
    } else if (avgConfidence >= 0.5 || needsClarification) {
      // Medium confidence or needs clarification: show message
      const message =
        needsClarification && result.needsClarification?.[0]
          ? result.needsClarification[0].suggestedQuestion
          : "Please review your medications and ensure they're listed clearly";
      setClarificationMessage(message);
      track("lp.ai_medication_clarification_needed", {
        sessionId: sessionIdRef.current,
        avgConfidence,
        clarificationType: needsClarification ? "specific" : "general",
        message,
        originalText:
          needsClarification && result.needsClarification?.[0]
            ? result.needsClarification[0].originalText
            : undefined,
        variant: "ai",
        flowType: "seamless",
        landing_page_id: landingPageId || "unknown",
        variant_id: variantId || landingPageId || "unknown",
        master_id: masterId || landingPageId || "unknown",
        prescreener_id: prescreenerId || landingPageId || "unknown",
      });
      // Stay in input mode
      reset();
      setIsProcessingFlow(false);
      isSubmittingRef.current = false;

      // Run validation in background (non-blocking)
      confirmMedications(mappedMedications, text).catch((error) => {
        console.error(
          "[Seamless Flow] Background validation error (medium confidence):",
          error
        );
        withScope((scope) => {
          scope.setExtra('sessionId', sessionIdRef.current);
          scope.setExtra('confidence', avgConfidence);
          scope.setExtra('needsClarification', needsClarification);
          scope.setTag('component', 'AIMedicationEntry');
          scope.setTag('error', 'background_validation_medium');
          captureException(error);
        });
      });
    } else {
      // Low confidence: generic retry
      setClarificationMessage(
        "We couldn't understand your entry. Please list each medication on a separate line (e.g., 'Aspirin' or 'Metformin for diabetes')"
      );
      track("lp.ai_medication_low_confidence_retry", {
        sessionId: sessionIdRef.current,
        avgConfidence,
        variant: "ai",
        flowType: "seamless",
        landing_page_id: landingPageId || "unknown",
        variant_id: variantId || landingPageId || "unknown",
        master_id: masterId || landingPageId || "unknown",
        prescreener_id: prescreenerId || landingPageId || "unknown",
      });
      reset();
      setIsProcessingFlow(false);
      isSubmittingRef.current = false;

      // Run validation in background (non-blocking)
      confirmMedications(mappedMedications, originalText).catch((error) => {
        console.error(
          "[Seamless Flow] Background validation error (low confidence):",
          error
        );
        withScope((scope) => {
          scope.setExtra('sessionId', sessionIdRef.current);
          scope.setExtra('confidence', avgConfidence);
          scope.setTag('component', 'AIMedicationEntry');
          scope.setTag('error', 'background_validation_low');
          captureException(error);
        });
      });
    }
  };

  const handleAutoAdvance = (meds: RecognizedMedication[]) => {
    // Prevent double auto-advance
    if (hasAutoAdvancedRef.current) return;
    hasAutoAdvancedRef.current = true;

    // Save answer data
    const answerData = buildAnswerData(meds, originalText);
    onAnswer(typedQuestion.id, JSON.stringify(answerData));

    // Show success animation
    setShowSuccessAnimation(true);

    // Auto-advance after showing success message
    successTimeoutRef.current = setTimeout(() => {
      if (onNext) {
        onNext();
      }
    }, 1500); // Show success for 1.5 seconds
  };

  const handleNoMedications = () => {
    // Check business logic first (handles minimumCategories)
    const decision = makeBusinessDecision([], typedQuestion.aiConfig);

    track("lp.ai_medication_business_decision", {
      sessionId: sessionIdRef.current,
      decision: decision.decision,
      flaggedCount: 0,
      clarificationCount: 0,
      medicationCount: 0,
      variant: "ai",
      flowType: "no_medications_button",
      landing_page_id: landingPageId || "unknown",
      variant_id: variantId || landingPageId || "unknown",
      master_id: masterId || landingPageId || "unknown",
      prescreener_id: prescreenerId || landingPageId || "unknown",
    });

    if (decision.decision === "fail") {
      const message =
        decision.reasoning ||
        typedQuestion.disqualifyMessage ||
        "This study has specific medication requirements.";
      if (onDisqualify) {
        onDisqualify(message);
      }

      // Save answer data AFTER onDisqualify so it isn't overwritten
      const answerData = {
        medications: {},
        rawText: "",
      };
      onAnswer(typedQuestion.id, JSON.stringify(answerData));
      return;
    }

    // Save in the same format as medications, but with empty data
    const answerData = {
      medications: {},
      rawText: "",
    };
    onAnswer(typedQuestion.id, JSON.stringify(answerData));

    if (onNext) onNext();
  };

  // Handle back navigation - always return to edit mode
  useEffect(() => {
    // If user navigates back to this question, reset to input mode
    if (
      status !== AIStatus.IDLE &&
      status !== AIStatus.TYPING &&
      status !== AIStatus.PROCESSING
    ) {
      reset();
    }
  }, [status, reset]);

  // Track when errors are displayed to user
  useEffect(() => {
    if (error) {
      withScope((scope) => {
        scope.setExtra('displayedError', error);
        scope.setExtra('sessionId', sessionIdRef.current);
        scope.setExtra('aiStatus', status);
        scope.setTag('component', 'AIMedicationEntry');
        scope.setTag('error', 'user_facing');
        captureException(new Error(`AI medication error: ${error}`));
      });

      track("lp.ai_medication_error_displayed", {
        sessionId: sessionIdRef.current,
        error: error,
        variant: "ai",
        landing_page_id: landingPageId || "unknown",
        variant_id: variantId || landingPageId || "unknown",
        master_id: masterId || landingPageId || "unknown",
        prescreener_id: prescreenerId || landingPageId || "unknown",
      });
    }
  }, [error]);

  return (
    <div className="space-y-6 relative">
      {/* Question header */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {typedQuestion.question}
        </h3>
        {typedQuestion.helpText && (
          <p className="text-sm text-gray-500">{typedQuestion.helpText}</p>
        )}
      </div>

      {/* Clarification message */}
      {clarificationMessage && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 animate-slideDown" role="status" aria-live="polite">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-amber-800">{clarificationMessage}</p>
          </div>
        </div>
      )}

      {/* Single medication input interface */}
      <MedicationInput
        onSubmit={handleAnalyzeMedications}
        onNoMedications={handleNoMedications}
        status={isProcessingFlow ? AIStatus.PROCESSING : status}
        placeholder="e.g. ozempic and prilosec"
        initialText={originalText}
      />

      {/* Success message - shows inline below the input */}
      {showSuccessAnimation && (
        <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 animate-slideDown" role="status" aria-live="assertive">
          <div className="flex items-center justify-center">
            <svg
              className="w-5 h-5 text-green-600 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-green-700 font-medium">
              Medications are eligible
            </p>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert" aria-live="assertive">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-red-400 mt-0.5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-red-700">{error}</p>
              <p className="text-sm text-red-600 mt-1">
                You can try again or enter your medications manually.
              </p>
              <div className="mt-3 flex space-x-3">
                <button
                  onClick={reset}
                  className="text-red-600 underline hover:text-red-800"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Back button - always available when allowed */}
      {allowBack && onBack && (
        <div className="mt-4 flex justify-between items-center">
          <button
            type="button"
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium focus:outline-none focus:underline"
          >
            ← Back
          </button>
        </div>
      )}
    </div>
  );
};

// Build serializable answer data from recognized medications
function buildAnswerData(
  medications: RecognizedMedication[],
  rawText: string
): { medications: Record<string, string>; rawText: string } {
  const gridData: Record<string, string> = {};
  medications.forEach((med) => {
    const key = med.name || "unknown_medication";
    gridData[key] = med.gridValue || `${med.name}_${med.status}`;
  });
  return { medications: gridData, rawText };
}
